import { Badge, Input, Switch } from '@dittolive/anvil'
import { useFormik } from 'formik'
import React from 'react'
import * as Yup from 'yup'

export const CREATE_OR_UPDATE_WEBHOOK_FORM_ID = 'create-or-update-webhook-form'

/**
 * Reserved provider name that cannot be used for webhooks
 */
const PLAYGROUND_AUTH_PROVIDER = 'replication-auth'

const buildSchema = (existingNames: string[]) =>
  Yup.object().shape({
    name: Yup.string()
      .required('A webhook name is required')
      .notOneOf([PLAYGROUND_AUTH_PROVIDER], 'This is a reserved provider name')
      .min(3, 'Must be longer than 3 characters.')
      .max(50, 'Must be shorter than 50 characters')
      .test(
        'valid-name-with-spaces',
        'The webhook name must be longer than 3 characters, excluding spaces.',
        (value: string | null | undefined): boolean =>
          value ? value.trim().length > 3 : true,
      )
      .test(
        'valid-name',
        'A webhook with this name already exists',
        (value: string | null | undefined): boolean => {
          if (value) {
            return !existingNames
              .map((name) => name.toLocaleLowerCase().trim())
              .includes(value.toLocaleLowerCase().trim())
          }
          return true
        },
      ),
    url: Yup.string()
      .required('The webhook URL is required')
      .min(3, 'Must be longer than 3 characters.')
      .url('Must be a valid URL'),
  })

type Props = {
  /**
   * The webhook that is being edited, if any. If this is not provided, the form
   * will be in "create" mode.
   */
  webhook?: {
    name: string
    url: string
    legacy?: boolean
  }
  /** Current list of other webhooks, to avoid duplicated names being created. */
  existingWebhookNames: string[]
  /** Callback to handle the form submit after validation passes. */
  onSubmit: (name: string, url: string, legacy: boolean) => void
  /** Whether DQL auth webhook feature is enabled (shows legacy toggle) */
  enableDqlAuthWebhook?: boolean
}

export default function CreateOrUpdateWebhookForm({
  webhook,
  existingWebhookNames,
  onSubmit,
  enableDqlAuthWebhook = false,
}: Props) {
  const formik = useFormik({
    initialValues: {
      // If enableDqlAuthWebhook is true, set fallback to false, otherwise set it to true
      legacy: webhook?.legacy ?? (enableDqlAuthWebhook ? false : true),
      name: webhook?.name || '',
      url: webhook?.url || '',
    },
    validationSchema: buildSchema(existingWebhookNames),
    onSubmit: (values, { resetForm }) => {
      onSubmit(values.name.trim(), values.url.trim(), values.legacy)
      resetForm()
    },
    enableReinitialize: true,
  })

  const toggleLegacy = (checked: boolean) => {
    formik.setFieldValue('legacy', checked)
  }

  return (
    <form id={CREATE_OR_UPDATE_WEBHOOK_FORM_ID} onSubmit={formik.handleSubmit}>
      <fieldset disabled={formik.isSubmitting} className="flex flex-col gap-4">
        <Input
          placeholder="Webhook name"
          description="Give your webhook a memorable name"
          label="Name"
          id="name"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.name}
          errorMessage={!!formik.touched.name ? formik.errors.name : undefined}
          data-testid="webhookNameInput"
        />

        <Input
          placeholder="https://api.example.com/webhook"
          label="URL"
          id="url"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.url}
          errorMessage={!!formik.touched.url ? formik.errors.url : undefined}
          data-testid="webhookUrlInput"
        />

        {enableDqlAuthWebhook && (
          <div className="mt-2">
            <p className="text-foreground mb-0.5 leading-none">
              Legacy webhook <Badge colorScheme="sunset">Deprecated</Badge>
            </p>
            <div className="flex w-full flex-row items-center justify-between">
              <span className="text-foreground-subtle leading-none">
                Use the legacy query builder format{' '}
              </span>
              <Switch
                checked={formik.values.legacy}
                onCheckedChange={toggleLegacy}
              />
            </div>
          </div>
        )}
      </fieldset>
    </form>
  )
}
